<template>
  <ErrorDialog :dialogVisible="errorDialogVisible" :errorMessage="errorMessage"
    @update:dialogVisible="errorDialogVisible = $event" />

  <v-alert
    v-if="successMessage"
    class="mb-4"
    closable
    density="comfortable"
    type="success"
    variant="tonal"
    @click:close="successMessage = null"
  >
    {{ successMessage }}
    <div v-if="latestFulfillmentResult" class="mt-3">
      <v-alert
        v-for="(advisory, index) in latestFulfillmentResult.advisories"
        :key="`${advisory.code || 'advisory'}-${index}`"
        class="mt-3"
        density="comfortable"
        :type="getFulfillmentAdvisoryType(advisory)"
        variant="tonal"
      >
        <div>{{ advisory.message }}</div>
        <div v-if="formatFulfillmentAdvisoryDetail(advisory)" class="text-caption mt-1">
          {{ formatFulfillmentAdvisoryDetail(advisory) }}
        </div>
      </v-alert>
      <div v-if="latestFulfillmentResult.operation?.ticket_number">
        <strong>Operation ticket number:</strong> {{ latestFulfillmentResult.operation.ticket_number }}
      </div>
      <div v-if="latestFulfillmentResult.applicationStatus">
        <strong>Enhanced Air Ticket status:</strong> {{ latestFulfillmentResult.applicationStatus }}
      </div>
      <v-table
        v-if="latestFulfillmentResult.summaryItems.length > 0"
        class="mt-3"
        density="compact"
      >
        <thead>
          <tr>
            <th>Document</th>
            <th>Type</th>
            <th>Name</th>
            <th>Total</th>
            <th>Committed</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(summaryItem, index) in latestFulfillmentResult.summaryItems"
            :key="`${summaryItem.DocumentNumber || 'summary'}-${index}`"
          >
            <td>{{ summaryItem.DocumentNumber || '-' }}</td>
            <td>{{ summaryItem.DocumentType || '-' }}</td>
            <td>{{ formatFulfillmentSummaryName(summaryItem) || '-' }}</td>
            <td>{{ summaryItem.TotalAmount || '-' }}</td>
            <td>{{ formatFulfillmentCommitted(summaryItem.committed) }}</td>
          </tr>
        </tbody>
      </v-table>
    </div>
  </v-alert>

  <v-alert v-if="providerDocumentReviewOperation" type="warning" class="mb-4" closable @click:close="providerDocumentReviewOperation = null">
    The provider changed. The operation kept its saved document requirement. Review it with an administrator.
    <v-btn variant="text" @click="openProviderDocumentsDialog(providerDocumentReviewOperation)">Review provider documents</v-btn>
  </v-alert>

  <v-dialog v-model="dialog" max-width="600px" density="compact">
    <v-form ref="form" validate-on="submit blur" @submit.prevent="submitForm">
      <v-card density="compact">
        <v-card-item>
          <v-card-title>
            <strong>
              {{ dialog }} Operation
            </strong></v-card-title>
        </v-card-item>
        <v-card-text>
          <v-row>
            <v-col>
              <v-autocomplete v-model="operation.transaction" :items="config.detail.operation.transaction"
                auto-select-first :rules="[v => !!v || 'Transaction is required']" required
                label="Transaction"></v-autocomplete>
            </v-col>
            <v-col>
              <v-autocomplete v-model="operation.type" :items="config.detail.operation.type" auto-select-first
                :rules="[v => !!v || 'Type is required']" required label="Type"></v-autocomplete>
            </v-col>
          </v-row>
          <v-autocomplete v-model="operation.op" :items="config.detail.operation.op"
            :rules="[v => !!v || 'Operation is required']" label="Operation" auto-select-first required></v-autocomplete>
          <v-autocomplete v-model="operation.platform" :items="config.detail.operation.platform" label="Platform"
            :rules="[v => !!v || 'Platform is required']" required auto-select-first></v-autocomplete>
          <v-autocomplete v-model="operation.provider" label="Provider" :items="config.detail.operation.provider"
            auto-select-first></v-autocomplete>
          <v-alert v-if="providerChangeNeedsDocumentReview" type="warning" class="mb-3">
            Changing provider keeps this operation's saved document requirement. Review Provider documents after saving.
          </v-alert>
          <v-text-field v-model="operation.customer" label="Customer" :rules="[v => !!v || 'Customer is required']"
            required></v-text-field>
          <v-row>
            <v-col>
              <v-text-field v-model="operation.reservation_code" label="Reservation Code"
                :rules="[v => !!v || 'Reservation Code is required']"></v-text-field>
            </v-col>
            <v-col>
              <v-text-field v-model="operation.ticket_number" label="Ticket Number"></v-text-field>
            </v-col>
          </v-row>
          <v-textarea v-model="operation.passengers" label="Passengers" :rules="[v => !!v || 'Passengers is required']"
            required></v-textarea>
          <v-textarea v-model="operation.description" label="Description"></v-textarea>
          <v-row>
            <v-col>

              <v-text-field v-model.number="operation.cost_total" label="Total Cost" type="number"
                :rules="[(v) => v !== null || 'Field is required', (v) => !isNaN(Number(v)) || 'Input must be a number']">
              </v-text-field>
            </v-col>
            <v-col>

              <v-text-field v-model.number="operation.cost_iva"
                :label="'IVA ' + (operation.cost_total / 1.16 * 0.16).toFixed(2)" type="number">
              </v-text-field>
            </v-col>
          </v-row>
          <v-text-field v-model.number="operation.sale_price"
            :label="`Sale Price ` + Math.ceil(operation.cost_total + 348) + ' / ' + Math.ceil(operation.cost_total + 580) + ' / ' + Math.ceil(operation.cost_total + 1000)"
            type="number"
            :rules="[
              (v) => v !== null || 'Field is required',
              (v) => !isNaN(Number(v)) || 'Input must be a number',
              (v) => operation.transaction === 'expense' || Number(v) >= operation.cost_total || 'Input must be greater than or equal to cost_total']"></v-text-field>
          <v-autocomplete v-model="operation.currency" label="Currency ¤" :items="config.detail.currencies"
            auto-select-first></v-autocomplete>
          <v-text-field v-model="localDate" label="Sale Date" readonly></v-text-field>
          <div class="d-flex justify-space-between mb-4 align-center">
            <v-btn color="primary" @click="this.operation.date = new Date()">Set Date to Today</v-btn>
            <v-spacer></v-spacer>
            <v-btn color="orange" v-if="dialog === 'Create'" @click="this.operation.date = null">Delete Date</v-btn>
            <v-spacer></v-spacer>
            <v-checkbox v-model="operation.credit" label="Credit" color="warning" hide-details
              :inline="true"></v-checkbox>
            <v-checkbox v-model="operation.invoiceRequired" label="Invoice?" color="error" hide-details
              :inline="true"></v-checkbox>
          </div>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-btn color="blue darken-1" text @click="dialog = null">Close</v-btn>
          <v-btn color="blue darken-1" text @click="cleanForm">Clean</v-btn>
          <v-spacer></v-spacer>
          <v-btn :color="dialog === 'Create' ? 'primary' : 'warning'" type="submit" :loading="loading">{{ dialog
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>

  <v-dialog v-model="createByPnrDialog" max-width="520px" density="compact">
    <v-form ref="createByPnrForm" validate-on="submit blur" @submit.prevent="submitCreateByPnr">
      <v-card density="compact">
        <v-card-item>
          <v-card-title>
            <strong>Create Operations By PNR</strong>
          </v-card-title>
        </v-card-item>
        <v-card-text>
          <div class="text-body-2 mb-4">
            This calls the backend `createOperationByPNR` route and creates one or more operations from the Sabre booking.
          </div>
          <v-text-field
            v-model="createByPnr.confirmationId"
            :rules="[v => !!String(v || '').trim() || 'PNR / Confirmation ID is required']"
            label="PNR / Confirmation ID"
            required
          ></v-text-field>
          <v-text-field
            v-model="createByPnr.targetPcc"
            hint="Optional. Only use this when the booking should be retrieved under a specific Sabre PCC."
            label="Target PCC"
            persistent-hint
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-btn color="blue darken-1" text @click="createByPnrDialog = false">Close</v-btn>
          <v-btn color="blue darken-1" text @click="createByPnr = initialCreateByPnrState()">Clean</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" type="submit" :loading="createByPnrLoading">Create</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>

  <v-dialog v-model="fulfillDialogVisible" max-width="720px" density="compact">
    <v-form ref="fulfillOperationForm" validate-on="submit blur" @submit.prevent="submitFulfillOperation">
        <v-card density="compact">
          <v-card-item>
            <v-card-title>
              <strong>Fulfill Operation</strong>
            </v-card-title>
          </v-card-item>
        <v-card-text>
          <div class="text-body-2 mb-4">
            This calls the backend `POST /operation/:id/fulfill` route with the Enhanced Air Ticket request contract. Enter a fresh 6-digit authenticator code and provide only the supported request JSON fields below.
          </div>

          <v-row v-if="operationTo">
            <v-col cols="12" md="4">
              <v-text-field :model-value="operationTo.reservation_code" label="PNR" readonly></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field :model-value="operationTo.pq" label="Sabre Reference" readonly></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field :model-value="operationTo.ticket_number" label="Current Ticket Number" readonly></v-text-field>
            </v-col>
          </v-row>

          <v-text-field
            :model-value="fulfillRequest.authenticatorCode"
            @update:modelValue="fulfillRequest.authenticatorCode = normalizeAuthenticatorCode($event)"
            :rules="[v => String(v || '').length === 6 || 'Fresh 6-digit authenticator code is required']"
            :counter="6"
            inputmode="numeric"
            label="Authenticator Code"
            maxlength="6"
            required
          ></v-text-field>

          <v-textarea
            v-model="fulfillRequest.requestBody"
            auto-grow
            hint="JSON only. `authenticatorCode` is added from the field above. Supported FOP types: CASH, CHECK, PAYMENTCARD, VIRTUAL_CARD."
            label="Enhanced Air Ticket Request Body (JSON)"
            persistent-hint
            rows="10"
          ></v-textarea>
          <div class="text-caption mt-2">
            Supported fields: `targetPcc`, `bookingSource`, `receivedFrom`, `designatePrinters`, `formsOfPayment`,
            `payment.primaryFormOfPayment`, `serviceFee.overrideAmount`, `serviceFee.currencyCode`,
            `serviceFee.description`, `serviceFee.customerReference`, `generateSingleInvoice`,
            `acceptNegotiatedFare`, `acceptPriceChanges`, `priceQuoteExpirationMethod`,
            `backDatePriceQuoteMethod`, `ticketingQualifiers.endorsements`, `ticketingQualifiers.tourCode`,
            `ticketingQualifiers.tourCodeOverrides`.
          </div>
          <div class="text-caption mt-1">
            Unsupported Booking Management-specific fulfill fields are rejected before submit.
          </div>
          <div class="text-caption mt-1">
            Traveler and PQ selection come from the operation. `ticketingQualifiers.travelerIndices` and
            `ticketingQualifiers.priceQuoteRecordIds` are not sent from the client.
          </div>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-btn color="blue darken-1" text @click="fulfillDialogVisible = false">Close</v-btn>
          <v-btn color="blue darken-1" text @click="resetFulfillDialog">Clean</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" type="submit" :loading="fulfillLoading">Fulfill</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>

  <v-sheet>
    <div class="d-flex  mt-2 align-center mb-4">
      <v-btn color="blue" @click="dialog = `Create`; this.operation = { ...this.operation };">+ Operation</v-btn>
      <v-btn color="indigo" class="ml-2" @click="openCreateByPnrDialog">+ Create By PNR</v-btn>
      <v-btn color="green" class="ml-2"
        @click="createPaymentFromCustomer(selected_operations); selected_operations.splice(0, selected_operations.length);">
        + Payment From Customer {{ selectedTotal.sale_price }}
      </v-btn>
      <OperationMergeDialog ref="operationMerge" :can-merge="canMergeOperations" :operations="operations"
        :selected-ids="selected_operations" @merged="onOperationsMerged" />
      <OdooSyncDialog ref="odooSync" :can-sync="canMergeOperations" :busy="odooSyncRunning"
        :selected-ids="selected_operations" :operations="operations"
        @synced="replaceOperation" @syncing="odooSyncRunning = $event" />
      <v-btn v-if="canMergeOperations" color="teal" class="ml-2"
        :disabled="!selected_operations.length || odooSyncRunning" @click="$refs.odooSync.open()">
        Sync selected to Odoo
      </v-btn>
      <span class="ml-2">
        {{ this.selected_operations.length }} SELECTED
      </span>
      <v-checkbox
        v-model="showCostTotal"
        label="Show Cost Total"
        density="compact"
        hide-details
        class="ml-3 flex-grow-0"
      />
      <v-spacer></v-spacer>
      <v-btn color="orange" class="ml-2"
        @click="createPaymentToProvider(selected_operations); selected_operations.splice(0, selected_operations.length);">
        + Payment to Provider {{ selectedTotal.cost_total }}
      </v-btn>
    </div>

    <div class="d-flex  mt-2 align-center">
      <v-text-field v-model="search" clearable density="compact" hide-details placeholder="Search"
        prepend-inner-icon="mdi-magnify" style="max-width: 300px;" variant="solo"></v-text-field>
      <v-text-field density="compact"  v-model="itin_pnr" placeholder="PNR" hide-details style="max-width: 100px" class="ml-2" variant="solo" bg-color="red"></v-text-field>
      <v-text-field density="compact"  v-model="itin_lastname" placeholder="Last Name" hide-details style="max-width: 150px" class="ml-2" variant="solo" bg-color="red"></v-text-field>
      <v-btn color="red" class="ml-2" :href="`https://services.tripcase.com/new/itineraryChron.html?lastName=${itin_lastname}&segment=print&action=printPreview&pnr=${itin_pnr}&language=7`" target="_blank">简体</v-btn>
      <v-btn color="red" class="ml-2" :href="`https://services.tripcase.com/new/itineraryChron.html?lastName=${itin_lastname}&segment=print&action=printPreview&pnr=${itin_pnr}&language=1`" target="_blank">ESP</v-btn>
      <v-btn color="red" class="ml-2" :href="`https://services.tripcase.com/new/itineraryChron.html?lastName=${itin_lastname}&segment=print&action=printPreview&pnr=${itin_pnr}&language=5`" target="_blank">ENG</v-btn>
      <v-btn color="red" class="ml-2" :href="`https://services.tripcase.com/new/itineraryChron.html?lastName=${itin_lastname}&segment=print&action=printPreview&pnr=${itin_pnr}&language=8`" target="_blank">繁体</v-btn>
      <v-spacer></v-spacer>
      <span @click="groupBy = groupBy.length > 0 ? [] : [{ key: 'customer', order: 'asc' }]">GroupByDK</span>
    </div>

  </v-sheet>
  <v-divider class="mb-2 mt-2"></v-divider>

  <v-dialog v-model="amountDialogVisible" max-width="300px">
    <v-card>
      <v-card-title class="headline">Enter an Amount from Customer</v-card-title>
      <v-card-text>
        <v-text-field v-model.number="paymentAmount" type="number" label="Number"></v-text-field>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="green darken-1" text
          @click="amountDialogVisible = false; this.operation.tmpCustomerAmount = paymentAmount;">OK</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>


  <v-dialog v-model="providerAmountDialogVisible" max-width="300px">
    <v-card>
      <v-card-title class="headline">Enter an Amount for Provider</v-card-title>
      <v-card-text>
        <v-text-field v-model.number="paymentAmount" type="number" label="Number"></v-text-field>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="green darken-1" text
          @click="providerAmountDialogVisible = false; this.operation.tmpProviderAmount = paymentAmount;">OK</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-data-table :items="filteredOperations" :headers="visibleHeaders" :items-per-page="itemsPerPage" density="compact" show-select
    :group-by="groupBy" :hover="true" :row-props="getRowProps" v-model="selected_operations" item-value="_id"
    style="font-family: monospace;">
    <template v-slot:group-header="{ item, columns, toggleGroup, isGroupOpen }">
      <tr>
        <td :colspan="columns.length">
          <VBtn size="small" variant="text" :icon="isGroupOpen(item) ? '$expand' : '$next'" @click="toggleGroup(item)">
          </VBtn>
          {{ item.value }} ({{ item.items.length }}) {{ item.items.reduce((acc, cur) => {
            let totalPayment = 0;
            if (cur.raw.payments_from_customer && cur.raw.payments_from_customer.length > 0) {
              totalPayment = cur.raw.payments_from_customer.reduce((acc, payment) => {
                if (payment.payment.movement) {
                  return acc + payment.amount;
                } else {
                  return acc;
                }
              }, 0);
            }
            return acc + (cur.raw.sale_price - totalPayment);
          }, 0).toFixed(2) }} / {{ item.items.reduce((acc, cur) => {
            let totalPayment = 0;
            if (cur.raw.payments_to_provider && cur.raw.payments_to_provider.length > 0) {
              totalPayment = cur.raw.payments_to_provider.reduce((acc, payment) => {
                if (payment.payment.movement) {
                  return acc + payment.amount;
                } else {
                  return acc;
                }
              }, 0);
            }
            return acc + (cur.raw.cost_total + totalPayment);
          }, 0).toFixed(2) }}

        </td>
      </tr>
    </template>
    <template v-slot:[`item.credit`]="{ item }">
      <v-chip variant="flat" v-if="item.credit && !isPaid(item)" label color="orange" class="">CR</v-chip>
      <v-chip variant="flat" v-if="isPaid(item)" label color="green" class="">PA</v-chip>
    </template>
    <template v-slot:[`item.payments_from_customer`]="{ item }">
      <p v-for="payment in item.payments_from_customer" :key="payment._id" class="text-decoration-underline text-right"
        :style="{ background: intToRGB(hashCode(payment.payment._id)), 'color': getContrastColor(intToRGB(hashCode(payment.payment._id))) }">
        <a @click="currentPayment = payment; paymentDialog = true;" link="">{{ payment.amount }}<span
            v-if="payment.payment.movement" class="">/P<span v-if="!payment.payment.movement._id">∎</span></span><span
            v-else>&nbsp;&nbsp;</span></a>
      </p>
    </template>
    <template v-slot:[`item.payments_to_provider`]="{ item }">
      <p v-for="payment in item.payments_to_provider" :key="payment._id" class="text-decoration-underline text-right"> <a
          @click="currentPayment = payment; paymentDialog = true; this.operation = item;" link="">{{ payment.amount
          }}<span v-if="payment.payment.movement">/P<span v-if="!payment.payment.movement._id">∎</span></span><span v-else
            style="background-color: orange">&nbsp;&nbsp;</span></a>
      </p>
    </template>

    <template v-slot:[`item.actions1`]="{ item }">
      <v-chip variant="flat" v-if="isPaidToProvider(item)" label color="primary" class="">PP</v-chip>
    </template>

    <template v-slot:[`item.sale_price`]="{ item }">
      <p><a @click="this.operation = item; amountDialogVisible = true;" link=""
          class="text-decoration-underline">$</a><span v-if="item.tmpCustomerAmount && item.tmpCustomerAmount !== 0">{{
            item.tmpCustomerAmount }}/</span>{{ item.sale_price }}
      </p>
    </template>
    <template v-slot:[`item.invoiceRequired`]="{ item }">
      <v-chip variant="flat" v-if="item.invoiceRequired" label color="yellow-accent-3" class="">✓</v-chip>
    </template>
    <template v-slot:[`item.cost_total`]="{ item }">
      <p><a @click="this.operation = item; providerAmountDialogVisible = true;" link=""
          class="text-decoration-underline">$</a><span v-if="item.tmpProviderAmount && item.tmpProviderAmount !== 0">{{
            item.tmpProviderAmount }}/</span>{{ item.cost_total }}
      </p>
    </template>
    <template v-slot:[`item.sales_agent`]="{ item }">
      {{ item.sales_agent.lastName }}/{{ item.sales_agent.firstName }}
    </template>
    <template v-slot:[`item.reservation_code`]="{ item }">
      <div class="d-flex align-center" v-if="item.platform === 'sabre'">
        <v-menu>
          <template v-slot:activator="{ props }">
            <span v-bind="props" class="text-decoration-underline">{{ item.reservation_code }}</span>
          </template>
          <v-list>
            <v-list-item>
              <v-list-item-title size="small">
                <a target="_blank"
                  :href="'https://services.tripcase.com/new/itineraryChron.html?lastName=' + item.passengers.split('/')[0] + '&segment=print&action=printPreview&pnr=' + item.reservation_code + '&language=7'">
                  简体
                </a>
              </v-list-item-title>
            </v-list-item>
            <v-list-item>
              <v-list-item-title size="small">
                <a target="_blank"
                  :href="'https://services.tripcase.com/new/itineraryChron.html?lastName=' + item.passengers.split('/')[0] + '&segment=print&action=printPreview&pnr=' + item.reservation_code + '&language=8'">
                  繁体
                </a>
              </v-list-item-title>
            </v-list-item>
            <v-list-item>
              <v-list-item-title>
                <a target="_blank"
                  :href="'https://services.tripcase.com/new/itineraryChron.html?lastName=' + item.passengers.split('/')[0] + '&segment=print&action=printPreview&pnr=' + item.reservation_code + '&language=1'">
                  ES
                </a>
              </v-list-item-title>
            </v-list-item>
            <v-list-item>
              <v-list-item-title>
                <a target="_blank"
                  :href="'https://services.tripcase.com/new/itineraryChron.html?lastName=' + item.passengers.split('/')[0] + '&segment=print&action=printPreview&pnr=' + item.reservation_code + '&language=5'">
                  EN
                </a>
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
      <div class="d-flex align-center" v-if="item.provider === 'volaris'">
        <v-menu>
          <template v-slot:activator="{ props }">
            <span v-bind="props" class="text-decoration-underline">{{ item.reservation_code }}</span>
          </template>
          <v-list>
            <v-list-item>
              <v-list-item-title size="small">
                <a target="_blank"
                  :href="'https://www.volaris.com?lastName=' + item.passengers.split('/')[0] + '&recordLocator=' + item.reservation_code + '&language=es-MX'">
                  PNR volaris
                </a>
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
      <div class="d-flex align-center" v-if="item.provider === 'vivaaerobus'">
        <v-menu>
          <template v-slot:activator="{ props }">
            <span v-bind="props" class="text-decoration-underline">{{ item.reservation_code }}</span>
          </template>
          <v-list>
            <v-list-item>
              <v-list-item-title size="small">
                <a target="_blank"
                  :href="'https://www.vivaaerobus.com/es-mx/manage/trip-details?lastName=' + item.passengers.split('/')[0] + '&pnr=' + item.reservation_code">
                  PNR viva
                </a>
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
      <span v-if="item.platform !== 'sabre' && item.provider !== 'volaris' && item.provider !== 'vivaaerobus'">{{
        item.reservation_code }}</span>
    </template>

    <template v-slot:[`item.date`]="{ item }">
      <pre>{{ $filters.toMexicoCityTime(item.date) }}</pre>
    </template>


    <template v-slot:[`item.odoo_so`]="{ item }">
      {{ item.odoo_so }}
    </template>

    <template v-slot:[`item.ticket_number`]="{ item }">
      <v-menu style="font-family: monospace;" :close-on-content-click="false"
        v-if="( item.platform === 'sabre' || item.platform === 'amadeus' ) && item.ticket_number">
        <template v-slot:activator="{ props }">
          <pre v-bind="props" class="text-decoration-underline"
            @click="tktinfo = ''; getDocDetailFromCarriers(item.ticket_number.split('/')[0])">{{ item.ticket_number }}<span v-bind="props" @click="tktinfo = ''; getDocDetail(item.ticket_number.split('/')[0])">.</span></pre>
        </template>
        <v-list>
          <v-list-item>
            <v-list-item-title size="small">
              <pre>{{ tktinfo }}</pre>
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <pre v-else>{{ item.ticket_number }}</pre>
    </template>

    <template v-slot:[`item.passengers`]="{ item }">
      <v-menu style="font-family: monospace;" :close-on-content-click="false"
        v-if="item.passengers.split('\n').length > 1">
        <template v-slot:activator="{ props }">
          <pre v-bind="props" class="text-decoration-underline">{{ item.passengers.split('\n')[0] }}</pre>
        </template>
        <v-list>
          <v-list-item>
            <v-list-item-title size="small">
              <pre>{{ item.passengers }}</pre>
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <pre v-else>{{ item.passengers }}</pre>
    </template>
    <template v-slot:[`item.description`]="{ item }">
      <v-menu style="font-family: monospace;" :close-on-content-click="false"
        v-if="item.description.split('\n').length > 1">
        <template v-slot:activator="{ props }">
          <pre v-bind="props" class="text-decoration-underline">{{ item.description.split('\n')[0] }}</pre>
        </template>
        <v-list>
          <v-list-item>
            <v-list-item-title size="small">
              <pre>{{ item.description }}</pre>
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <pre v-else>{{ item.description }}</pre>
    </template>


    <template v-slot:[`item.actions`]="{ item }">
      <div class="d-flex align-center">
        <v-icon size="small" @click="openModifyDialog(item)">
          mdi-pencil
        </v-icon>
      </div>
    </template>
    <template v-slot:[`item.actions0`]="{ item }">
      <v-menu style="font-family: monospace;">
        <template v-slot:activator="{ props }">
          <v-icon size="small" v-bind="props">
            mdi-menu
          </v-icon>
        </template>
        <v-list>
          <v-list-item v-if="isFulfillableOperation(item)" @click="openFulfillDialog(item)">
            <v-list-item-title size="small">
              <v-icon size="small">
                mdi-ticket
              </v-icon>
              Fulfill Sabre
            </v-list-item-title>
          </v-list-item>
          <v-list-item
            @click="createPaymentToProvider; selected_operations.push(item._id); createPaymentToProvider(selected_operations); selected_operations.splice(0, selected_operations.length);">
            <v-list-item-title size="small">
              <v-icon size="small">
                mdi-sync
              </v-icon>
              Payment to Provider (solo)
            </v-list-item-title>
          </v-list-item>
          <v-list-item :disabled="odooSyncRunning || Boolean(item.odoo_so)" @click="getOdooSo(item)">
            <v-list-item-title size="small">
              <v-icon size="small">
                mdi-sync
              </v-icon>
              Sync Odoo So
            </v-list-item-title>
          </v-list-item>
          <v-list-item v-if="canMergeOperations" @click="$refs.operationMerge.openHistory(item._id)">
            <v-list-item-title>Merge history</v-list-item-title>
          </v-list-item>
          <v-list-item @click="openProviderDocumentsDialog(item)">
            <v-list-item-title size="small">
              <v-icon size="small">
                mdi-file-document-outline
              </v-icon>
              Provider documents
            </v-list-item-title>
          </v-list-item>
          <v-list-item @click="operationTo = item; operationActionDialogVisible = 'unarchive'" v-if=" item.archived ">
            <v-list-item-title size="small">
              <v-icon size="small">
                mdi-archive-check
              </v-icon>
              Unarchive
            </v-list-item-title>
          </v-list-item>
          <v-list-item @click="operationTo = item; operationActionDialogVisible = 'archive'" v-else>
            <v-list-item-title size="small">
              <v-icon size="small">
                mdi-archive-check
              </v-icon>
              Archive
            </v-list-item-title>
          </v-list-item>
          <v-list-item>
            <v-list-item-title size="small">
              <v-icon size="small">
                mdi-calendar
              </v-icon>
              UpdatedAt: {{ $filters.toMexicoCityTime(item.updatedAt) }}
            </v-list-item-title>
          </v-list-item>
          <v-list-item>
            <v-list-item-title size="small">
              <v-icon size="small">
                mdi-calendar
              </v-icon>
              CreatedAt: {{ $filters.toMexicoCityTime(item.createdAt) }}
            </v-list-item-title>
          </v-list-item>

          <v-list-item @click="operationTo = item; operationActionDialogVisible = 'delete';">
            <v-list-item-title size="small">
              <v-icon size="small">
                mdi-delete
              </v-icon>
              Delete
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </template>
  </v-data-table>
  <pre>
  </pre>
  <v-dialog v-model=" paymentDialog " max-width="600px" style="font-family: monospace;">
    <v-card>
      <v-card-title class="headline">
        Payment Details
      </v-card-title>
      <v-card-text>
        <p>_id: {{ currentPayment.payment._id }} <span @dblclick="deletePayment(currentPayment.payment)"
            v-if=" !currentPayment.payment.movement ">-</span></p>
        <p>Amount: {{ currentPayment.amount }} / {{ currentPayment.payment.amount }} </p>
        <p>Currency: {{ currentPayment.payment.currency }}</p>
        <p>Created By: {{ currentPayment.payment.created_by }}</p>
        <p>Created At: {{ $filters.toMexicoCityTime(currentPayment.payment.createdAt) }}</p>
        <p>Updated At: {{ $filters.toMexicoCityTime(currentPayment.payment.updatedAt) }}</p>
        <p>Reference: {{ currentPayment.payment.reference }}</p>
        <p v-if=" currentPayment.payment.movement && currentPayment.payment.movement.tsLiquidacion "
          class="text-decoration-underline">Liquidation Time: {{ $filters.toMexicoCityTime(new
          Date(Number(currentPayment.payment.movement.tsLiquidacion))) }}</p>
        <p>Movement: <span v-if=" !currentPayment.payment.movement "
            @click="showMovementForm = !showMovementForm">+/-</span>
        <pre v-if=" currentPayment.payment.movement ">{{ JSON.stringify(currentPayment.payment.movement, null, 2) }}</pre>
        </p>
        <div v-if=" showMovementForm && !currentPayment.payment.movement " class="mt-2">
          <v-form ref="movementForm" validate-on="submit blur" @submit.prevent=" submitMovementForm ">
            <v-text-field label="Fecha Operacion" v-model=" movementForm.fechaOperacion " type="date"></v-text-field>

            <v-autocomplete v-model=" movementForm.tipoPago " :items=" ['transfer', 'cash', 'check'] " auto-select-first
              label="Tipo Pago"></v-autocomplete>

            <v-autocomplete v-model=" movementForm.institucionOrdenante " :items=" config.detail.banks " auto-select-first
              item-title="name" item-value="id" label="Institucion Ordenante"
              v-if=" currentPayment.payment.amount < 0 "></v-autocomplete>
            <v-autocomplete v-model=" movementForm.cuentaOrdenante " :items=" config.detail.bankAccounts "
              auto-select-first v-if=" currentPayment.payment.amount < 0 " label="Cuenta Ordenante"></v-autocomplete>
            <v-text-field label="Nombre Ordenante" v-model=" movementForm.nombreOrdenante "
              v-if=" currentPayment.payment.amount < 0 "></v-text-field>

            <v-autocomplete v-model=" movementForm.institucionBeneficiaria " :items=" config.detail.banks "
              auto-select-first v-if=" currentPayment.payment.amount > 0 " item-title="name" item-value="id"
              label="Institucion Beneficiaria"></v-autocomplete>
            <v-autocomplete v-model=" movementForm.cuentaBeneficiario " :items=" config.detail.bankAccounts "
              v-if=" currentPayment.payment.amount > 0 " auto-select-first label="Cuenta Beneficiario"></v-autocomplete>
            <v-text-field label="Nombre Beneficiario" v-model=" movementForm.nombreBeneficiario "
              v-if=" currentPayment.payment.amount > 0 "></v-text-field>

            <v-text-field label="Monto" v-model.number=" movementForm.monto " type="number"></v-text-field>
            <v-autocomplete v-model=" movementForm.moneda " :items=" ['MXN', 'USD', 'CNY'] " auto-select-first
              label="Moneda"></v-autocomplete>
            <v-text-field 
            label="Tipo de Cambio"
            v-model.number=" movementForm.tipoCambio " type="number"></v-text-field>
            {{(currentPayment.payment.amount/movementForm.monto).toFixed(20)}}
            <v-text-field label="Clave Rastreo" v-model=" movementForm.claveRastreo "></v-text-field>
            <v-text-field label="Concepto Pago" v-model=" movementForm.conceptoPago "></v-text-field>
            <v-text-field label="Referencia Numerica" v-model.number=" movementForm.referenciaNumerica "
              type="number"></v-text-field>
            <v-btn type="submit">Submit</v-btn>
            <v-btn color="blue darken-1 ml-2" text @click="showMovementForm = false">Close</v-btn>
          </v-form>
        </div>

        <v-divider class="mt-8"></v-divider>
        <div v-if=" currentPayment.payment.amount > 0 && !currentPayment.payment.movement && !showMovementForm ">
          <p class="mt-4">
            <strong>墨西哥启程旅游账号信息：</strong><br />
            <br />
            银行/BANCO: STP<br />
            帐户名/TITULAR: A DE RH INTERNACIONAL SA DE CV<br />
            转账账号/CLABE: 646180395200021292<br />
            <br />
            <strong>请您转账时：</strong><br />
            <br />
          <ul class="ml-4">
            <li>在备注栏<strong class="text-red-lighten-1">"CONCEPTO"</strong>填写：<strong class="pa-2"
                style="background-color: #eee; color: red; font-size: 25px;">{{ currentPayment.payment.reference }}</strong></li>
            <li class="mt-4">输入正确的金额/MONTO: {{ currentPayment.payment.amount }}</li>
          </ul>
          <br />
          <strong>注意：</strong>
          <br />
          <br />
          <ul class="ml-4">
            <li style="color: red; font-size: 20px;">需要正确输入上述金额和备注才会转账成功。</li>
            <li>上述账号只支持手机或者网上银行转账使用。</li>
            <li>不支持现金存款。</li>
            <li>如有任何疑问，请联系我们客服。</li>
          </ul>

          </p>

          <v-divider class="mt-8"></v-divider>
          <p class="mt-4">
            <strong>CUENTA BANCARIA DE VAMONOZ VIAJES:</strong><br />
            <br />
            BANCO: STP<br />
            TITULAR: A DE RH INTERNACIONAL SA DE CV<br />
            CLABE: 646180395200021292<br />
            <br />
            <strong>Cuando se realiza la transferencia：</strong><br />
            <br />
          <ul class="ml-4">
            <li>Escriba en <strong class="text-red-lighten-1">"CONCEPTO"</strong>：<strong class="pa-2"
                style="background-color: #eee; color: red; font-size: 25px;">{{ currentPayment.payment.reference }}</strong></li>
            <li class="mt-4">Ingrese en Monto/Importe: {{ currentPayment.payment.amount }}</li>
          </ul>
          <br />
          <strong>Notas：</strong>
          <br />
          <br />
          <ul class="ml-4">
            <li style="color: red;">Solo acepta pagos referenciados con monto exacto.</li>
            <li>La cuenta solo acepta transferencia SPEI.</li>
            <li>No acepta pago/depósito en efectivo.</li>
            <li>Cualquier duda, favor de contactarnos.</li>
          </ul>
          </p>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="green darken-1" text @click="paymentDialog = false">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <ProviderDocumentsDialog v-model="providerDocumentsDialog" :operation="providerDocumentsOperation"
    :admin="canMergeOperations" @changed="updateProviderDocumentMetadata" />

  <v-dialog v-model=" operationActionDialogVisible " max-width="500px">
    <v-card>
      <v-card-title class="headline">Confirm {{ operationActionDialogVisible }} ?</v-card-title>
      <v-card-text>Do you really want to <strong>{{ operationActionDialogVisible }}</strong> this operation?</v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="green darken-1" text @click="operationActionDialogVisible = false">Cancel</v-btn>
        <v-btn color="green darken-1" text @click="operationAction(operationActionDialogVisible)">{{
          operationActionDialogVisible }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ProviderDocumentsDialog from './ProviderDocumentsDialog.vue';
import ErrorDialog from './ErrorDialog.vue';
import OperationMergeDialog from './OperationMergeDialog.vue';
import OdooSyncDialog from './OdooSyncDialog.vue';

import axios from 'axios';
import { getApiErrorMessage } from '../utils/api';
import { paymentPayload, selectedPaymentTotals } from '../utils/paymentAmounts.mjs';
import {
  buildOperationSearchIndex,
  createOperationSearchQuery,
  matchesOperationSearchIndex,
} from '../utils/operationSearch.mjs';

const SUPPORTED_FULFILL_FIELDS = new Set([
  'targetPcc',
  'bookingSource',
  'receivedFrom',
  'designatePrinters',
  'formsOfPayment',
  'payment',
  'serviceFee',
  'generateSingleInvoice',
  'acceptNegotiatedFare',
  'acceptPriceChanges',
  'priceQuoteExpirationMethod',
  'backDatePriceQuoteMethod',
  'ticketingQualifiers',
]);

const SUPPORTED_FULFILL_PAYMENT_FIELDS = new Set([
  'primaryFormOfPayment',
]);

const SUPPORTED_FULFILL_SERVICE_FEE_FIELDS = new Set([
  'overrideAmount',
  'currencyCode',
  'description',
  'customerReference',
]);

const SUPPORTED_FULFILL_TICKETING_QUALIFIER_FIELDS = new Set([
  'endorsements',
  'tourCode',
  'tourCodeOverrides',
]);

const SUPPORTED_FULFILL_FOP_TYPES = ['CASH', 'CHECK', 'PAYMENTCARD', 'VIRTUAL_CARD'];
const SUPPORTED_PRICE_QUOTE_METHODS = ['Reprice', 'Override', 'Quit'];

function isPlainObject(value) {
  return Boolean(value) && !Array.isArray(value) && typeof value === 'object';
}

export default {
  name: 'OperationPage',
  props: ['operation_filter'],
  components: {
    ErrorDialog,
    ProviderDocumentsDialog,
    OperationMergeDialog,
    OdooSyncDialog,
  },
  data() {
    return {
      itin_pnr: '',
      itin_lastname: '',
      tktinfo: '',
      config: JSON.parse(localStorage.getItem('config')),
      search: '',
      dialog: null,
      createByPnrDialog: false,
      fulfillDialogVisible: false,
      loading: false,
      createByPnrLoading: false,
      odooSyncRunning: false,
      fulfillLoading: false,
      operation: this.initialFormState(),
      createByPnr: this.initialCreateByPnrState(),
      fulfillRequest: this.initialFulfillState(),
      operations: [],
      operationSearchIndexById: new Map(),
      selected_operations: [],
      canMergeOperations: false,
      itemsPerPage: 25,
      errorDialogVisible: false,
      errorMessage: null,
      successMessage: null,
      providerDocumentReviewOperation: null,
      latestFulfillmentResult: null,
      paymentDialog: false,
      currentPayment: null,
      providerDocumentsDialog: false,
      providerDocumentsOperation: null,
      operationTo: null,
      operationActionDialogVisible: null,
      amountDialogVisible: false,
      providerAmountDialogVisible: false,
      paymentAmount: 0,
      movementForm: {
        fechaOperacion: '',
        institucionOrdenante: '',
        institucionBeneficiaria: '',
        claveRastreo: '',
        monto: '',
        nombreOrdenante: '',
        cuentaOrdenante: '',
        nombreBeneficiario: '',
        cuentaBeneficiario: '',
        conceptoPago: '',
        referenciaNumerica: '',
        tipoPago: '',
      },
      showMovementForm: false,
      groupBy: [],
      showCostTotal: true,
      headers: [
        { title: 'Type', value: 'type' },
        { title: 'Op', value: 'op' },
        { title: 'Platform', value: 'platform' },
        { title: 'Provider', value: 'provider' },
        { title: 'DK', value: 'customer' },
        { title: 'Date', value: 'date' },
        { title: 'PNR', value: 'reservation_code' },
        { title: 'Ticket Number', value: 'ticket_number' },
        { title: 'Passengers', value: 'passengers' },
        { title: 'Description', value: 'description' },
        { title: 'Cost Total', value: 'cost_total' },
        { title: 'Sale Price', value: 'sale_price' },
        { title: '¤', value: 'currency' },
        { title: 'Invoice?', value: 'invoiceRequired' },
        { title: 'A', key: 'actions', sortable: false },
        { title: 'P/C', value: 'credit' },
        { title: 'A', key: 'actions0', sortable: false },
        { title: 'Payments from Customer', value: 'payments_from_customer' },
        { title: 'Payments to Provider', value: 'payments_to_provider' },
        { title: 'A', key: 'actions1', sortable: false },
        { title: 'Cost IVA', value: 'cost_iva' },
        { title: 'Sale IVA', value: 'sale_iva' },
        { title: 'Sales Agent', value: 'sales_agent' },
        { title: 'Odoo SO', value: 'odoo_so' },
        { title: 'T', value: 'transaction' },
        { title: '_id', value: '_id' },
      ],
    }
  },
  async created() {
    axios.get(process.env.VUE_APP_API_BASE_URL + '/operation/merge/capabilities')
      .then(({ data }) => { this.canMergeOperations = data.canMerge === true; })
      .catch(() => { this.canMergeOperations = false; });
    // watch the params of the route to fetch the data again
    this.$watch(
      () => this.$route.params,
      async () => {
        try {
          const response = await axios.get(process.env.VUE_APP_API_BASE_URL + '/operation/' + this.$route.params.filter, {
            params: this.$route.query
          });
          this.operations = Array.isArray(response.data)
            ? response.data.map(operation => this.normalizeOperation(operation) || operation)
            : [];
          this.rebuildOperationSearchIndex();
        } catch (error) {
          console.error('Failed to fetch operations:', error.message);
        }
      },
      // fetch the data when the view is created and the data is
      // already being observed
      { immediate: true }
    )
  },
  async mounted() {
    console.log('mounted');

  },
  computed: {
    providerChangeNeedsDocumentReview() {
      if (this.dialog !== 'Modify' || !this.operation?._id) return false;
      const saved = this.operations.find(item => item._id === this.operation._id);
      const normalized = value => String(value || '').trim().toUpperCase();
      return Boolean(saved && normalized(saved.provider) !== normalized(this.operation.provider));
    },
    visibleHeaders() {
      return this.showCostTotal
        ? this.headers
        : this.headers.filter(header => header.value !== 'cost_total');
    },
    filteredOperations() {
      const searchQuery = createOperationSearchQuery(this.search);

      if (!searchQuery) {
        return this.operations;
      }

      if (searchQuery.preferTicketMatches) {
        const ticketMatches = this.operations.filter(operation =>
          matchesOperationSearchIndex(this.getOperationSearchIndex(operation), searchQuery, 'ticket')
        );

        if (ticketMatches.length > 0) {
          return ticketMatches;
        }
      }

      return this.operations.filter(operation =>
        matchesOperationSearchIndex(this.getOperationSearchIndex(operation), searchQuery)
      );
    },
    selectedTotal() {
      try {
        return selectedPaymentTotals(this.operations.filter(operation => this.selected_operations.includes(operation._id)));
      } catch {
        return { sale_price: '—', cost_total: '—' };
      }
    },
    localDate: {
      get() {
        return this.operation.date ? this.$filters.toMexicoCityTime(this.operation.date) : null;
      },
      set(value) {
        this.operation.date = new Date(value);
      }
    }
  },
  methods: {
    async onOperationsMerged(result) {
      this.selected_operations = [];
      this.latestFulfillmentResult = null;
      this.successMessage = `Merge completed. Retained operation: ${result.target}. Audit: ${result.auditId}`;
      try {
        const response = await axios.get(process.env.VUE_APP_API_BASE_URL + '/operation/' + this.$route.params.filter,
          { params: this.$route.query });
        this.operations = Array.isArray(response.data)
          ? response.data.map(operation => this.normalizeOperation(operation) || operation) : [];
        this.rebuildOperationSearchIndex();
        const target = this.operations.find(operation => operation._id === result.target);
        if (target) this.openModifyDialog(target);
      } catch (error) {
        this.errorMessage = 'Merge completed, but refreshing the list failed. Refresh the page before editing. ' + getApiErrorMessage(error, '');
        this.errorDialogVisible = true;
      }
    },
    initialFormState() {
      return {
        transaction: 'sale',
        platform: '',
        date: '',
        type: '',
        op: '',
        customer: '',
        provider: '',
        sales_agent: '',
        sale_price: null,
        reservation_code: '',
        ticket_number: '',
        passengers: '',
        description: '#',
        cost_total: null,
        cost_iva: null,
        odoo_so: '',
        odoo_po: '',
        payments_to_provider: [],
        payments_from_customer: [],
        doc_to_customer: '',
        credit: false,
        // Add more fields as needed
      };
    },
    initialCreateByPnrState() {
      return {
        confirmationId: this.itin_pnr || '',
        targetPcc: '',
      };
    },
    initialFulfillPayload() {
      return {
        formsOfPayment: [{ type: 'CASH' }],
        payment: { primaryFormOfPayment: 1 },
      };
    },
    initialFulfillState() {
      return {
        authenticatorCode: '',
        requestBody: JSON.stringify(this.initialFulfillPayload(), null, 2),
      };
    },
    normalizeAuthenticatorCode(value) {
      return String(value || '').replace(/\D/g, '').slice(0, 6);
    },
    rebuildOperationSearchIndex() {
      const searchIndexById = new Map();

      for (const operation of this.operations) {
        if (!operation?._id) {
          continue;
        }

        searchIndexById.set(operation._id, buildOperationSearchIndex(operation));
      }

      this.operationSearchIndexById = searchIndexById;
    },
    updateOperationSearchIndex(operation) {
      if (!operation?._id) {
        return null;
      }

      const searchIndex = buildOperationSearchIndex(operation);
      this.operationSearchIndexById.set(operation._id, searchIndex);
      return searchIndex;
    },
    removeOperationSearchIndex(operationId) {
      if (!operationId) {
        return;
      }

      this.operationSearchIndexById.delete(operationId);
    },
    getOperationSearchIndex(operation) {
      if (!operation?._id) {
        return buildOperationSearchIndex(operation);
      }

      const cachedIndex = this.operationSearchIndexById.get(operation._id);

      if (cachedIndex) {
        return cachedIndex;
      }

      this.updateOperationSearchIndex(operation);
      return this.operationSearchIndexById.get(operation._id);
    },
    openCreateByPnrDialog() {
      this.createByPnr = this.initialCreateByPnrState();
      this.createByPnrDialog = true;
    },
    openFulfillDialog(item) {
      this.operationTo = item;
      this.successMessage = null;
      this.latestFulfillmentResult = null;
      this.fulfillRequest = this.initialFulfillState();
      this.fulfillDialogVisible = true;
    },
    resetFulfillDialog() {
      this.fulfillRequest = this.initialFulfillState();
      this.$refs.fulfillOperationForm?.resetValidation();
    },
    assertSupportedFulfillKeys(source, supportedKeys, path) {
      const unsupportedKeys = Object.keys(source).filter(key => !supportedKeys.has(key));

      if (unsupportedKeys.length > 0) {
        throw new Error(`Unsupported fulfill request field: ${path}.${unsupportedKeys[0]}`);
      }
    },
    normalizeSupportedEnumValue(value, supportedValues, path) {
      const normalizedValue = String(value || '').trim();
      const matchedValue = supportedValues.find(
        supportedValue => supportedValue.toLowerCase() === normalizedValue.toLowerCase()
      );

      if (!matchedValue) {
        throw new Error(`Unsupported value for ${path}. Supported values: ${supportedValues.join(', ')}`);
      }

      return matchedValue;
    },
    sanitizeFulfillRequestBody(parsedBody) {
      this.assertSupportedFulfillKeys(parsedBody, SUPPORTED_FULFILL_FIELDS, 'request');

      const sanitizedBody = {};
      const assignIfPresent = (key, transform = value => value) => {
        if (Object.prototype.hasOwnProperty.call(parsedBody, key)) {
          sanitizedBody[key] = transform(parsedBody[key]);
        }
      };

      assignIfPresent('targetPcc', value => String(value || '').trim().toUpperCase());
      assignIfPresent('bookingSource', value => String(value || '').trim());
      assignIfPresent('receivedFrom', value => String(value || '').trim());
      assignIfPresent('designatePrinters', value => {
        if (!Array.isArray(value)) {
          throw new Error('designatePrinters must be an array.');
        }

        return value.map((printer, index) => {
          if (!isPlainObject(printer)) {
            throw new Error(`designatePrinters[${index}] must be an object.`);
          }

          this.assertSupportedFulfillKeys(printer, new Set(['profileNumber']), `designatePrinters[${index}]`);
          return {
            profileNumber: printer.profileNumber,
          };
        });
      });
      assignIfPresent('formsOfPayment', value => {
        if (!Array.isArray(value)) {
          throw new Error('formsOfPayment must be an array.');
        }

        return value.map((formOfPayment, index) => {
          if (!isPlainObject(formOfPayment)) {
            throw new Error(`formsOfPayment[${index}] must be an object.`);
          }

          return {
            ...formOfPayment,
            type: this.normalizeSupportedEnumValue(
              formOfPayment.type,
              SUPPORTED_FULFILL_FOP_TYPES,
              `formsOfPayment[${index}].type`
            ),
          };
        });
      });
      assignIfPresent('payment', value => {
        if (!isPlainObject(value)) {
          throw new Error('payment must be an object.');
        }

        this.assertSupportedFulfillKeys(value, SUPPORTED_FULFILL_PAYMENT_FIELDS, 'payment');
        return {
          primaryFormOfPayment: value.primaryFormOfPayment,
        };
      });
      assignIfPresent('serviceFee', value => {
        if (!isPlainObject(value)) {
          throw new Error('serviceFee must be an object.');
        }

        this.assertSupportedFulfillKeys(value, SUPPORTED_FULFILL_SERVICE_FEE_FIELDS, 'serviceFee');

        const serviceFee = {};

        ['overrideAmount', 'currencyCode', 'description', 'customerReference'].forEach(field => {
          if (Object.prototype.hasOwnProperty.call(value, field)) {
            serviceFee[field] = value[field];
          }
        });

        return serviceFee;
      });
      assignIfPresent('generateSingleInvoice');
      assignIfPresent('acceptNegotiatedFare');
      assignIfPresent('acceptPriceChanges');
      assignIfPresent('priceQuoteExpirationMethod', value => this.normalizeSupportedEnumValue(
        value,
        SUPPORTED_PRICE_QUOTE_METHODS,
        'priceQuoteExpirationMethod'
      ));
      assignIfPresent('backDatePriceQuoteMethod', value => this.normalizeSupportedEnumValue(
        value,
        SUPPORTED_PRICE_QUOTE_METHODS,
        'backDatePriceQuoteMethod'
      ));
      assignIfPresent('ticketingQualifiers', value => {
        if (!isPlainObject(value)) {
          throw new Error('ticketingQualifiers must be an object.');
        }

        this.assertSupportedFulfillKeys(value, SUPPORTED_FULFILL_TICKETING_QUALIFIER_FIELDS, 'ticketingQualifiers');

        const ticketingQualifiers = {};

        ['endorsements', 'tourCode', 'tourCodeOverrides'].forEach(field => {
          if (Object.prototype.hasOwnProperty.call(value, field)) {
            ticketingQualifiers[field] = value[field];
          }
        });

        return ticketingQualifiers;
      });

      return Object.fromEntries(
        Object.entries(sanitizedBody).filter(([, value]) => value !== undefined)
      );
    },
    openModifyDialog(item) {
      this.dialog = 'Modify';
      this.operation = {
        ...item,
        payments_from_customer: [...(item.payments_from_customer || [])],
        payments_to_provider: [...(item.payments_to_provider || [])],
      };
    },
    isOperationRecord(record) {
      if (!record || !record._id) {
        return false;
      }

      const operationKeys = [
        'transaction',
        'platform',
        'customer',
        'reservation_code',
        'payments_from_customer',
        'payments_to_provider',
        'cost_total',
        'sale_price',
        'type',
        'op',
      ];

      return operationKeys.some(key => Object.prototype.hasOwnProperty.call(record, key));
    },
    isPaymentRecord(record) {
      if (!record || !record._id) {
        return false;
      }

      const paymentKeys = ['amount', 'reference', 'movement', 'currency', 'created_by'];
      return paymentKeys.some(key => Object.prototype.hasOwnProperty.call(record, key));
    },
    normalizeOperation(operation) {
      if (!this.isOperationRecord(operation)) {
        return null;
      }

      return {
        ...operation,
        payments_from_customer: Array.isArray(operation.payments_from_customer) ? operation.payments_from_customer : [],
        payments_to_provider: Array.isArray(operation.payments_to_provider) ? operation.payments_to_provider : [],
      };
    },
    isFulfillableOperation(operation) {
      const platform = String(operation?.platform || '').trim().toLowerCase();
      const reference = String(operation?.pq || '').trim();
      const ticketNumber = String(operation?.ticket_number || '').trim();

      return platform.startsWith('sabre')
        && !operation?.archived
        && Boolean(reference)
        && !ticketNumber;
    },
    extractFulfillOperationRecord(data) {
      if (this.isOperationRecord(data?.operation)) {
        return this.normalizeOperation(data.operation);
      }

      const [operation] = this.extractOperationRecords(data);
      return operation || null;
    },
    extractFulfillmentSummaryItems(data) {
      const summaryItems = data?.fulfillment?.AirTicketRS?.Summary;

      if (!Array.isArray(summaryItems)) {
        return [];
      }

      return summaryItems.filter(item => isPlainObject(item));
    },
    extractFulfillmentApplicationStatus(data) {
      return String(data?.fulfillment?.AirTicketRS?.ApplicationResults?.status || '').trim();
    },
    extractFulfillmentAdvisories(data) {
      if (!Array.isArray(data?.advisories)) {
        return [];
      }

      return data.advisories
        .map(advisory => {
          if (typeof advisory === 'string') {
            const message = advisory.trim();
            return message ? { code: '', severity: 'warning', message } : null;
          }

          if (!isPlainObject(advisory)) {
            return null;
          }

          const message = String(advisory.message || '').trim();

          if (!message) {
            return null;
          }

          return {
            code: String(advisory.code || '').trim(),
            severity: String(advisory.severity || 'warning').trim().toLowerCase(),
            message,
            expected: isPlainObject(advisory.expected) ? advisory.expected : null,
            issued: isPlainObject(advisory.issued) ? advisory.issued : null,
          };
        })
        .filter(Boolean);
    },
    buildFulfillmentResult(data, updatedOperation = null) {
      return {
        operation: updatedOperation || this.extractFulfillOperationRecord(data),
        applicationStatus: this.extractFulfillmentApplicationStatus(data),
        advisories: this.extractFulfillmentAdvisories(data),
        summaryItems: this.extractFulfillmentSummaryItems(data),
      };
    },
    formatFulfillmentSummaryName(summaryItem) {
      return [summaryItem?.FirstName, summaryItem?.LastName]
        .map(value => String(value || '').trim())
        .filter(Boolean)
        .join(' ');
    },
    formatFulfillmentCommitted(value) {
      if (value === true) {
        return 'Yes';
      }

      if (value === false) {
        return 'No';
      }

      const normalizedValue = String(value || '').trim();
      return normalizedValue || '-';
    },
    getFulfillmentAdvisoryType(advisory) {
      return advisory?.severity === 'info' ? 'info' : 'warning';
    },
    formatFulfillmentAdvisoryAmount(amountSide) {
      if (!isPlainObject(amountSide)) {
        return '';
      }

      const amount = String(amountSide.amount ?? '').trim();
      const currencyCode = String(amountSide.currencyCode || '').trim();

      return [amount, currencyCode].filter(Boolean).join(' ');
    },
    formatFulfillmentAdvisoryDetail(advisory) {
      const expectedAmount = this.formatFulfillmentAdvisoryAmount(advisory?.expected);
      const issuedAmount = this.formatFulfillmentAdvisoryAmount(advisory?.issued);

      if (!expectedAmount && !issuedAmount) {
        return '';
      }

      return `Expected: ${expectedAmount || '-'} | Issued: ${issuedAmount || '-'}`;
    },
    getFulfillFailureMessage(error) {
      const backendMessage = typeof error?.response?.data?.message === 'string'
        ? error.response.data.message.trim()
        : '';

      return backendMessage || getApiErrorMessage(error, 'Unknown error.');
    },
    extractOperationRecords(data) {
      if (!data) {
        return [];
      }

      if (Array.isArray(data)) {
        return data.filter(record => this.isOperationRecord(record));
      }

      if (Array.isArray(data.operations)) {
        return data.operations.filter(record => this.isOperationRecord(record));
      }

      if (this.isOperationRecord(data.operation)) {
        return [data.operation];
      }

      if (this.isOperationRecord(data)) {
        return [data];
      }

      return [];
    },
    extractPaymentRecords(data) {
      if (!data) {
        return [];
      }

      if (Array.isArray(data)) {
        return data.filter(record => this.isPaymentRecord(record));
      }

      if (Array.isArray(data.payments)) {
        return data.payments.filter(record => this.isPaymentRecord(record));
      }

      if (this.isPaymentRecord(data.payment)) {
        return [data.payment];
      }

      if (this.isPaymentRecord(data)) {
        return [data];
      }

      return [];
    },
    syncOperationReferences(updatedOperation) {
      if (this.operation && this.operation._id === updatedOperation._id) {
        this.operation = this.dialog === 'Modify'
          ? {
            ...updatedOperation,
            payments_from_customer: [...updatedOperation.payments_from_customer],
            payments_to_provider: [...updatedOperation.payments_to_provider],
          }
          : updatedOperation;
      }

      if (this.operationTo && this.operationTo._id === updatedOperation._id) {
        this.operationTo = updatedOperation;
      }
    },
    replaceOperation(updatedOperation) {
      const normalizedOperation = this.normalizeOperation(updatedOperation);

      if (!normalizedOperation) {
        return null;
      }

      const index = this.operations.findIndex(operation => operation._id === normalizedOperation._id);

      if (index === -1) {
        this.operations.unshift(normalizedOperation);
      } else {
        this.operations.splice(index, 1, normalizedOperation);
      }

      this.updateOperationSearchIndex(normalizedOperation);
      this.syncOperationReferences(normalizedOperation);
      return normalizedOperation;
    },
    replaceOperations(updatedOperations) {
      return updatedOperations
        .map(operation => this.replaceOperation(operation))
        .filter(Boolean);
    },
    syncOperationsFromResponse(data) {
      return this.replaceOperations(this.extractOperationRecords(data));
    },
    mergeOperationPatch(operationId, patch) {
      const index = this.operations.findIndex(operation => operation._id === operationId);

      if (index === -1) {
        return null;
      }

      const updatedOperation = this.normalizeOperation({
        ...this.operations[index],
        ...patch,
      });

      if (!updatedOperation) {
        return null;
      }

      this.operations.splice(index, 1, updatedOperation);
      this.updateOperationSearchIndex(updatedOperation);
      this.syncOperationReferences(updatedOperation);
      return updatedOperation;
    },
    replacePayment(updatedPayment) {
      if (!this.isPaymentRecord(updatedPayment)) {
        return null;
      }

      let currentPaymentLink = null;

      this.operations = this.operations.map(operation => {
        let hasChanges = false;

        const paymentsFromCustomer = (operation.payments_from_customer || []).map(paymentLink => {
          if (paymentLink.payment && paymentLink.payment._id === updatedPayment._id) {
            hasChanges = true;
            const updatedLink = { ...paymentLink, payment: updatedPayment };
            currentPaymentLink = updatedLink;
            return updatedLink;
          }
          return paymentLink;
        });

        const paymentsToProvider = (operation.payments_to_provider || []).map(paymentLink => {
          if (paymentLink.payment && paymentLink.payment._id === updatedPayment._id) {
            hasChanges = true;
            const updatedLink = { ...paymentLink, payment: updatedPayment };
            currentPaymentLink = updatedLink;
            return updatedLink;
          }
          return paymentLink;
        });

        if (!hasChanges) {
          return operation;
        }

        const updatedOperation = {
          ...operation,
          payments_from_customer: paymentsFromCustomer,
          payments_to_provider: paymentsToProvider,
        };

        this.updateOperationSearchIndex(updatedOperation);
        this.syncOperationReferences(updatedOperation);
        return updatedOperation;
      });

      if (this.currentPayment && this.currentPayment.payment && this.currentPayment.payment._id === updatedPayment._id) {
        this.currentPayment = currentPaymentLink || { ...this.currentPayment, payment: updatedPayment };
      }

      return updatedPayment;
    },
    clearTemporaryAmount(operationId, fieldName) {
      const index = this.operations.findIndex(operation => operation._id === operationId);

      if (index === -1) {
        return;
      }

      const updatedOperation = { ...this.operations[index] };
      delete updatedOperation[fieldName];
      this.operations.splice(index, 1, updatedOperation);
      this.updateOperationSearchIndex(updatedOperation);
      this.syncOperationReferences(updatedOperation);
    },
    attachPaymentsToOperations(payload, responseData, targetField, tempAmountField) {
      const payments = this.extractPaymentRecords(responseData);

      if (payments.length === 0) {
        return false;
      }

      payload.forEach((entry, index) => {
        const payment = payments.length === payload.length ? payments[index] : payments[0];
        const operationIndex = this.operations.findIndex(operation => operation._id === entry._id);

        if (!payment || operationIndex === -1) {
          return;
        }

        const updatedOperation = {
          ...this.operations[operationIndex],
          [targetField]: [
            ...(this.operations[operationIndex][targetField] || []),
            { amount: entry.amount, payment },
          ],
        };

        delete updatedOperation[tempAmountField];
        this.operations.splice(operationIndex, 1, updatedOperation);
        this.updateOperationSearchIndex(updatedOperation);
        this.syncOperationReferences(updatedOperation);
      });

      return true;
    },
    parseFulfillRequestBody() {
      const requestBody = String(this.fulfillRequest.requestBody || '').trim();

      if (!requestBody) {
        return {};
      }

      let parsedBody;

      try {
        parsedBody = JSON.parse(requestBody);
      } catch (error) {
        throw new Error('Fulfill request JSON is invalid.');
      }

      if (!parsedBody || Array.isArray(parsedBody) || typeof parsedBody !== 'object') {
        throw new Error('Fulfill request JSON must be a JSON object.');
      }

      if (Object.prototype.hasOwnProperty.call(parsedBody, 'authenticatorCode')) {
        throw new Error('Put authenticatorCode in the dedicated field, not inside the JSON body.');
      }

      return this.sanitizeFulfillRequestBody(parsedBody);
    },
    buildFulfillSuccessMessage(responseData, updatedOperation = null) {
      const operation = updatedOperation || this.extractFulfillOperationRecord(responseData);
      const documentNumbers = this.extractFulfillmentSummaryItems(responseData)
        .map(summaryItem => String(summaryItem?.DocumentNumber || '').trim())
        .filter(Boolean);

      if (operation?.ticket_number) {
        return `Operation fulfilled. Ticket number: ${operation.ticket_number}`;
      }

      if (documentNumbers.length > 0) {
        return `Operation fulfilled. Documents: ${[...new Set(documentNumbers)].join(' ')}`;
      }

      return 'Operation fulfilled successfully.';
    },
    async submitForm() {
      const { valid } = await this.$refs.form.validate();
      if (valid) {
        this.loading = true;
        if (this.dialog === 'Create') {
          try {
            const response = await axios.post(process.env.VUE_APP_API_BASE_URL + '/operation/create', this.operation);
            const [createdOperation] = this.syncOperationsFromResponse(response.data);
            if (!createdOperation) {
              this.operations.unshift(response.data);
              this.updateOperationSearchIndex(response.data);
            }
            this.operation = this.initialFormState();
            this.dialog = null;
          } catch (error) {
            this.errorMessage = 'Failed to create operation: ' + error.message + ' ' + error.response.data.message;
            this.errorDialogVisible = true;
          }
        } else if (this.dialog === 'Modify') {
          const providerChanged = this.providerChangeNeedsDocumentReview;
          try {
            const response = await axios.put(process.env.VUE_APP_API_BASE_URL + '/operation/' + this.operation._id, this.operation);
            this.replaceOperation(response.data);
            if (providerChanged) this.providerDocumentReviewOperation = response.data;
            this.dialog = null;
          } catch (error) {
            console.error(error);
            this.errorMessage = 'Failed to modify operation: ' + error.message + ' ' + error.response.data.message;
            this.errorDialogVisible = true;
          }
        }
        this.loading = false;
      }
    },
    async submitFulfillOperation() {
      const { valid } = await this.$refs.fulfillOperationForm.validate();

      if (!valid || !this.operationTo) {
        return;
      }

      let requestBody;

      try {
        requestBody = this.parseFulfillRequestBody();
      } catch (error) {
        this.errorMessage = error.message;
        this.errorDialogVisible = true;
        return;
      }

      this.fulfillLoading = true;
      this.successMessage = null;
      this.latestFulfillmentResult = null;

      try {
        const response = await axios.post(
          process.env.VUE_APP_API_BASE_URL + '/operation/' + this.operationTo._id + '/fulfill',
          {
            ...requestBody,
            authenticatorCode: this.fulfillRequest.authenticatorCode,
          }
        );

        const fulfilledOperation = this.extractFulfillOperationRecord(response.data);
        const updatedOperation = fulfilledOperation
          ? this.replaceOperation(fulfilledOperation)
          : this.syncOperationsFromResponse(response.data)[0] || null;

        this.latestFulfillmentResult = this.buildFulfillmentResult(response.data, updatedOperation);
        this.successMessage = this.buildFulfillSuccessMessage(response.data, updatedOperation);
        this.fulfillDialogVisible = false;
        this.fulfillRequest = this.initialFulfillState();
      } catch (error) {
        this.errorMessage = this.getFulfillFailureMessage(error);
        this.errorDialogVisible = true;
      } finally {
        this.fulfillLoading = false;
      }
    },
    async submitCreateByPnr() {
      const { valid } = await this.$refs.createByPnrForm.validate();

      if (!valid) {
        return;
      }

      this.createByPnrLoading = true;

      try {
        const payload = {
          confirmationId: String(this.createByPnr.confirmationId || '').trim().toUpperCase(),
        };

        const targetPcc = String(this.createByPnr.targetPcc || '').trim().toUpperCase();
        if (targetPcc) {
          payload.targetPcc = targetPcc;
        }

        const response = await axios.post(
          process.env.VUE_APP_API_BASE_URL + '/operation/createOperationByPNR',
          payload
        );

        this.extractOperationRecords(response.data)
          .slice()
          .reverse()
          .forEach(operation => {
            this.replaceOperation(operation);
          });

        this.itin_pnr = payload.confirmationId;
        this.createByPnr = this.initialCreateByPnrState();
        this.createByPnrDialog = false;
      } catch (error) {
        this.errorMessage = `Failed to create operations from PNR: ${getApiErrorMessage(error, 'Unknown error.')}`;
        this.errorDialogVisible = true;
      } finally {
        this.createByPnrLoading = false;
      }
    },
    cleanForm() {
      this.operation = this.initialFormState();
      this.$refs.form.reset();
    },
    async submitMovementForm() {
      const { valid } = await this.$refs.movementForm.validate();
      this.movementForm.__v = this.currentPayment.payment.__v;
      if (valid) {
        try {
          const response = await axios.put(process.env.VUE_APP_API_BASE_URL + '/payment/movement/' + this.currentPayment.payment._id, this.movementForm);
          if (this.syncOperationsFromResponse(response.data).length === 0) {
            this.replacePayment(response.data);
          }
          this.showMovementForm = false;
        } catch (error) {
          this.errorMessage = 'Failed to create movement: ' + error.message + ' ' + error.response.data.message;
          this.errorDialogVisible = true;
        }
      }
    },
    setDateToToday() {
      let today = new Date();
      let yyyy = today.getFullYear();
      let mm = String(today.getMonth() + 1).padStart(2, '0'); // Months are zero based
      let dd = String(today.getDate()).padStart(2, '0');
      this.operation.date = `${yyyy}-${mm}-${dd}`;
    },
    generateGradient(type, op, platform, provider) {
      const hash = this.hashCode(type + op + platform + provider);
      const color2 = this.intToRGB(hash); // Offset to create a different color for the gradient
      return `linear-gradient(to right, #ffffff, #ffffff, #${color2})`;
    },
    hexToRgb(hex) {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : null;
    },
    getContrastColor(hex) {
      const rgb = this.hexToRgb(hex);
      if (!rgb) {
        return null;
      }
      const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
      return luminance > 0.5 ? '#000' : '#fff';
    },
    hashCode(str) {
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
      }
      return hash;
    },
    intToRGB(i) {
      const c = (i & 0x00FFFFFF)
        .toString(16)
        .toUpperCase();
      return '#' + "00000".substring(0, 6 - c.length) + c;
    },
    openProviderDocumentsDialog(operation) {
      this.providerDocumentsOperation = operation;
      this.providerDocumentsDialog = true;
    },
    updateProviderDocumentMetadata(operation) {
      this.replaceOperation(operation);
    },
    shouldHighlightOperation(operation) {
      const costTotal = Number(operation.cost_total);
      const salePrice = Number(operation.sale_price);

      if (Number.isNaN(costTotal) || Number.isNaN(salePrice)) {
        return false;
      }

      return costTotal >= salePrice;
    },
    getRowProps({ item }) {
      return this.shouldHighlightOperation(item)
        ? { class: 'operation-row-warning' }
        : {};
    },
    isPaid(operation) {
      let sum = 0;
      let allPaymentsHaveMovement = true;

      operation.payments_from_customer.forEach(payment => {
        sum += payment.amount;
        if (!payment.payment.movement) {
          allPaymentsHaveMovement = false;
        }
      });

      return Number((operation.sale_price - sum).toFixed(5)) === 0 && allPaymentsHaveMovement;
    },
    isPaidToProvider(operation) {
      let sum = 0;
      let allPaymentsHaveMovement = true;

      operation.payments_to_provider.forEach(payment => {
        sum += payment.amount;
        if (!payment.payment.movement) {
          allPaymentsHaveMovement = false;
        }
      });

      return Number((operation.cost_total + sum).toFixed(5))=== 0 && allPaymentsHaveMovement;
    },
    async operationAction(action) {
      switch (action) {
        case 'archive':
          await this.archiveOperation(this.operationTo);
          break;
        case 'unarchive':
          await this.unarchiveOperation(this.operationTo);
          break;
        case 'delete':
          await this.deleteOperation(this.operationTo);
          break;
        default:
          // handle default case if necessary
          break;
      }
      this.operationActionDialogVisible = false;
    },
    async archiveOperation(item) {
      try {
        await axios.put(process.env.VUE_APP_API_BASE_URL + '/operation/' + item._id + "/archive", { archived: !item.archived, __v: item.__v });
        const index = this.operations.findIndex(operation => operation._id === item._id);
        if (index !== -1) {
          this.operations.splice(index, 1);
          this.removeOperationSearchIndex(item._id);
        }
      } catch (err) {
        this.errorMessage = 'Failed to archive operation: ' + err.message + ' ' + err.response.data.message;
        this.errorDialogVisible = true;
      }
    },
    async unarchiveOperation(item) {
      try {
        await axios.put(process.env.VUE_APP_API_BASE_URL + '/operation/' + item._id + "/archive", { archived: false, __v: item.__v });
        const index = this.operations.findIndex(operation => operation._id === item._id);
        if (index !== -1) {
          this.operations.splice(index, 1);
          this.removeOperationSearchIndex(item._id);
        }
      } catch (err) {
        this.errorMessage = 'Failed to unarchive operation: ' + err.message + ' ' + err.response.data.message;
        this.errorDialogVisible = true;
      }
    },
    async deleteOperation(item) {
      try {
        await axios.delete(process.env.VUE_APP_API_BASE_URL + '/operation/' + item._id + "/v/" + item.__v);
        const index = this.operations.findIndex(operation => operation._id === item._id);
        this.operations.splice(index, 1);
        this.removeOperationSearchIndex(item._id);
      } catch (err) {
        this.errorMessage = 'Failed to delete operation: ' + err.message + ' ' + err.response.data.message;
        this.errorDialogVisible = true;
      }
    },
    async getOdooSo(operation) {
      if (this.odooSyncRunning) return;
      this.odooSyncRunning = true;
      try {
        const response = await axios.get(process.env.VUE_APP_API_BASE_URL + '/operation/odoo/so/' + operation._id + "/v/" + operation.__v);
        if (this.syncOperationsFromResponse(response.data).length === 0) {
          this.mergeOperationPatch(operation._id, response.data);
        }
      } catch (error) {
        this.errorMessage = getApiErrorMessage(error, 'Failed to sync Odoo SO.');
        this.errorDialogVisible = true;
      } finally {
        this.odooSyncRunning = false;
      }
    },
    async deletePayment(payment) {
      try {
        await axios.delete(process.env.VUE_APP_API_BASE_URL + '/payment/' + payment._id + "/v/" + payment.__v);
        //find operations which contains this payment
        const operations = this.operations.filter(operation => {
          return operation.payments_from_customer.find(p => p.payment._id === payment._id)
            || operation.payments_to_provider.find(p => p.payment._id === payment._id);
        });
        //remove payment from operations
        operations.forEach(operation => {
          operation.payments_from_customer = operation.payments_from_customer.filter(p => p.payment._id !== payment._id);
          operation.payments_to_provider = operation.payments_to_provider.filter(p => p.payment._id !== payment._id);
          this.updateOperationSearchIndex(operation);
        });
        //close payment dialog
        this.paymentDialog = false;
      } catch (err) {
        this.errorMessage = 'Failed to delete operation: ' + err.message + ' ' + err.response.data.message;
        this.errorDialogVisible = true;
      }
    },
    async getDocDetail(ticket_number) {
      try {
        const response = await axios.get(process.env.VUE_APP_API_BASE_URL + '/operation/tkt/' + ticket_number);
        this.tktinfo = response.data;
      } catch (err) {
        this.errorMessage = 'Failed to get doc detail: ' + err.message + ' ' + err.response.data.message;
        this.errorDialogVisible = true;
      }
    },
    async getDocDetailFromCarriers(ticket_number) {
      try {
        const response = await axios.get(process.env.VUE_APP_API_BASE_URL + '/operation/tktFromCarriers/' + ticket_number);
        this.tktinfo = response.data;
      } catch (err) {
        this.errorMessage = 'Failed to get doc detail from carriers: ' + err.message + ' ' + err.response.data.message;
        this.errorDialogVisible = true;
      }
    },
    async createPaymentFromCustomer(selected_operations) {
      const operations = this.operations.filter(operation => selected_operations.includes(operation._id));

      try {
        const payload = paymentPayload(operations, 'customer');
        const response = await axios.post(process.env.VUE_APP_API_BASE_URL + '/payment/create/fromcustomer', payload);
        const updated = this.syncOperationsFromResponse(response.data).length > 0
          || this.attachPaymentsToOperations(payload, response.data, 'payments_from_customer', 'tmpCustomerAmount');

        if (updated) {
          payload.forEach(entry => this.clearTemporaryAmount(entry._id, 'tmpCustomerAmount'));
        }
      } catch (error) {
        this.errorMessage = getApiErrorMessage(error, 'Failed to create payment from customer.');
        this.errorDialogVisible = true;
      }
    },
    async createPaymentToProvider(selected_operations) {
      const operations = this.operations.filter(operation => selected_operations.includes(operation._id))

      try {

        operations.forEach(operation => {
          if (!operation.ticket_number || operation.ticket_number.trim() === "") {
            throw new Error('Ticket Number is required for operation: ' + operation._id);
          }
        });
      } catch (error) {
        this.errorMessage = 'Failed to create payment to provider: ' + error;
        this.errorDialogVisible = true;
        return
      }

      try {
        const payload = paymentPayload(operations, 'provider');
        const response = await axios.post(process.env.VUE_APP_API_BASE_URL + '/payment/create/toprovider', payload);
        const updated = this.syncOperationsFromResponse(response.data).length > 0
          || this.attachPaymentsToOperations(payload, response.data, 'payments_to_provider', 'tmpProviderAmount');

        if (updated) {
          payload.forEach(entry => this.clearTemporaryAmount(entry._id, 'tmpProviderAmount'));
        }
      } catch (error) {
        this.errorMessage = getApiErrorMessage(error, 'Failed to create payment to provider.');
        this.errorDialogVisible = true;
      }
    },
  },
}
</script>

<!--
  todo:
    - Paint payment from customer with different color depending value of its reference so user can identify if it applies to different operations
    - Rservation print button 

-->

<style>
.operation-row-warning {
  background-color: rgba(255, 193, 7, 0.16);
}
</style>
